import classNames from 'classnames';
import PreFilledByAITooltip from 'components/atoms/PreFilledByAITooltip.component';
import useWorldTheme from 'hooks/shared/useWorldTheme';
import { ReactNode } from 'react';
import { color } from 'types/shared/worlds';
import { getTracingEvents } from 'utils/amplitude';
import { TracingConfig } from 'utils/amplitude/amplitude.types';

interface Option {
  value: any;
  label: ReactNode;
  isDisabled?: boolean;
  selectedClassName?: string;
}

interface SegmentedToggleProps {
  value: any;
  options: Option[];
  onChange: (id: string) => void;
  className?: string;
  color?: color;
  disabled?: boolean;
  tracing?: TracingConfig<'onChange'>;
  aiTooltip?: {
    enabled?: boolean;
    description?: string | null;
  };
}

const colors: Record<string, string> = {
  bank: 'text-bank',
  expenses: 'text-expenses',
  primary: 'text-primary',
  taxes: 'text-taxes',
  revenues: 'text-revenues',
};

const SegmentedToggle: React.FC<SegmentedToggleProps> = ({
  value,
  options,
  onChange,
  className = '',
  color: propsColor,
  disabled,
  tracing,
  aiTooltip,
}) => {
  const theme = useWorldTheme();
  const color = colors[propsColor || theme] || colors.primary;

  const tracingEvents = getTracingEvents(tracing, { onChange });

  return (
    <div
      className={classNames(
        'flex w-full bg-primary-50 border border-primary-100 rounded-[10px] p-1',
        className,
      )}
    >
      {options.map((option) => {
        const isValueSelected = option.value === value;
        const isDisabled = option.isDisabled || disabled;
        const isAiTooltipEnabled =
          !isDisabled && isValueSelected && !!aiTooltip?.enabled;
        return (
          <PreFilledByAITooltip
            key={String(option.value)}
            enabled={isAiTooltipEnabled}
            description={isValueSelected ? aiTooltip?.description : undefined}
          >
            <label
              className={classNames(
                'text-sm flex-1 text-center py-1 cursor-pointer rounded-md font-semibold border border-transparent',
                {
                  'text-gray-400 cursor-not-allowed': isDisabled,
                  'text-primary-600': !isDisabled && !isValueSelected,
                  [option.selectedClassName || color]:
                    !isDisabled && isValueSelected,
                  'bg-transparent': !isValueSelected,
                  'bg-white shadow-[0px_2px_4px_0px_rgba(27,27,27,0.03),0px_6px_10px_0px_rgba(27,27,27,0.06)]':
                    isValueSelected,
                  'ai-button-bg-border': isAiTooltipEnabled,
                },
              )}
            >
              <input
                type="radio"
                name="segmented"
                value={option.value}
                checked={isValueSelected}
                onChange={() =>
                  !isDisabled && tracingEvents.onChange(option.value)
                }
                className="sr-only"
                disabled={isDisabled}
              />
              {option.label}
            </label>
          </PreFilledByAITooltip>
        );
      })}
    </div>
  );
};

export default SegmentedToggle;
